import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Home, Plus } from "lucide-react";
import { toast } from "react-hot-toast";
import { houseService } from "@/services/houseService";
import { hostService } from "@/services/hostService";
import { useAuthStore } from "@/store/authStore";
import {
  Alert,
  Button,
  EmptyState,
  Label,
  Select,
  type SelectOption,
} from "@/components/ui";
import { PageFrame, ListResultsCard } from "@/components/common";
import type { HouseCreate } from "@/types";
import { Permission } from "@/types";
import { CreateHouseModal } from "./components/CreateHouseModal";
import { HouseCard } from "./components/HouseCard";
import { getApiErrorMessage, toastApiError } from "@/lib/errorMessages";

export default function HousesPage() {
  const { t } = useTranslation("houses");
  const queryClient = useQueryClient();
  const { getCurrentUserId, isHost, hasPermission } = useAuthStore();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedHostId, setSelectedHostId] = useState<string>("");

  const selfHostId = getCurrentUserId();
  // Host picker needs platform house + host directory access (not role === ADMIN).
  const adminMode =
    hasPermission(Permission.HOUSES_READ_ANY) &&
    hasPermission(Permission.HOSTS_READ_ANY);
  const canCreateHouse = hasPermission(Permission.HOUSES_CREATE);

  const {
    data: hosts = [],
    isLoading: hostsLoading,
    error: hostsError,
    refetch: refetchHosts,
  } = useQuery({
    queryKey: ["hosts", "admin-list"],
    queryFn: () => hostService.getAll(),
    enabled: adminMode,
  });

  const hostOptions: SelectOption[] = useMemo(
    () =>
      hosts.map((h) => ({
        value: h.user_id,
        label: t("list.hostOptionLabel", { name: h.name }),
      })),
    [hosts, t],
  );

  const resolvedAdminHostId = useMemo(() => {
    if (!adminMode || hosts.length === 0) return "";
    if (selectedHostId && hosts.some((h) => h.user_id === selectedHostId)) {
      return selectedHostId;
    }
    return hosts[0].user_id;
  }, [adminMode, hosts, selectedHostId]);

  const effectiveHostId = adminMode ? resolvedAdminHostId : (selfHostId ?? "");

  const {
    data: housesData,
    isLoading: housesLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["houses", effectiveHostId],
    queryFn: () => houseService.listByHost(effectiveHostId),
    enabled: !!effectiveHostId && (!adminMode || hosts.length > 0),
  });

  const houses = housesData ?? [];

  const createHouseMutation = useMutation({
    mutationFn: (data: Omit<HouseCreate, "host_user_id">) =>
      houseService.create({ ...data, host_user_id: effectiveHostId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["houses", effectiveHostId] });
      queryClient.invalidateQueries({
        queryKey: ["host-houses", effectiveHostId],
      });
      toast.success(t("createSuccess"));
      setIsCreateModalOpen(false);
    },
    onError: (err) => {
      toastApiError(err, "createHouse");
    },
  });

  const pageTitle = adminMode
    ? hosts.length === 0 && !hostsLoading
      ? t("list.titleAdminNoHosts")
      : t("list.titleByHost")
    : t("list.titleMy");

  const pageDescription = adminMode
    ? hosts.length === 0 && !hostsLoading
      ? t("list.subtitleAdminNoHosts")
      : t("list.subtitleByHost")
    : t("list.subtitleMy");

  const headerActions = (
    <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-end">
      {adminMode ? (
        <div className="w-full sm:w-72">
          <Label htmlFor="admin-host-select">{t("list.hostLabel")}</Label>
          <Select
            id="admin-host-select"
            options={hostOptions}
            value={resolvedAdminHostId}
            onChange={(e) => setSelectedHostId(e.target.value)}
            aria-label={t("list.selectHostAria")}
          />
        </div>
      ) : null}
      {canCreateHouse ? (
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          {t("list.addHouse")}
        </Button>
      ) : null}
    </div>
  );

  if (!adminMode && !selfHostId) {
    return (
      <PageFrame title={t("list.titleMy")} description={t("list.subtitleMy")}>
        <Alert variant="warning">{t("list.hostIdMissing")}</Alert>
      </PageFrame>
    );
  }

  if (adminMode && hostsError) {
    return (
      <PageFrame
        title={t("list.titleByHost")}
        icon={Home}
        description={t("list.subtitleByHost")}
        error
        errorMessage={getApiErrorMessage(hostsError, "loadHosts")}
        onRetry={() => refetchHosts()}
      />
    );
  }

  if (adminMode && !hostsLoading && hosts.length === 0) {
    return (
      <PageFrame title={pageTitle} description={pageDescription}>
        <ListResultsCard
          title={
            <span className="flex items-center gap-2">
              <Home className="h-4 w-4 text-surface-400" />
              {t("list.titleAdminNoHosts")}
            </span>
          }
          contentClassName="p-4 sm:p-5"
          isEmpty
          emptyState={
            <EmptyState
              size="md"
              icon={Home}
              title={t("list.emptyTitle")}
              description={t("list.emptyHosts")}
            />
          }
        />
      </PageFrame>
    );
  }

  const isPageLoading = (adminMode && hostsLoading) || housesLoading;

  return (
    <PageFrame
      title={adminMode ? t("list.titleByHost") : t("list.titleMy")}
      icon={Home}
      description={
        adminMode ? t("list.subtitleByHost") : t("list.subtitleMy")
      }
      isLoading={isPageLoading}
      loadingMessage={t("list.loading")}
      error={!!error}
      errorMessage={getApiErrorMessage(error, "loadHouses")}
      onRetry={() => refetch()}
      actions={headerActions}
    >
      {houses.length === 0 ? (
        <ListResultsCard
          title={
            <span className="flex items-center gap-2">
              <Home className="h-4 w-4 text-surface-400" />
              {adminMode ? t("list.titleByHost") : t("list.titleMy")}
            </span>
          }
          contentClassName="p-4 sm:p-5"
          isEmpty
          emptyState={
            <EmptyState
              size="md"
              icon={Home}
              title={t("list.emptyTitle")}
              description={
                adminMode ? t("list.emptyDescAdmin") : t("list.emptyDescHost")
              }
              action={
                canCreateHouse ? (
                  <Button
                    onClick={() => setIsCreateModalOpen(true)}
                    leftIcon={<Plus className="h-4 w-4" />}
                  >
                    {t("list.addFirstHouse")}
                  </Button>
                ) : undefined
              }
            />
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {houses.map((house) => (
            <HouseCard
              key={house.id}
              house={house}
              showCommission={!isHost()}
            />
          ))}
        </div>
      )}

      {canCreateHouse ? (
        <CreateHouseModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={(data) => createHouseMutation.mutate(data)}
          isLoading={createHouseMutation.isPending}
        />
      ) : null}
    </PageFrame>
  );
}
